<template>
  <div class="h-100 d-flex justify-center align-center">
    <v-form @submit.prevent="" ref="form" v-model="valid" class="form_container" lazy-validation>
      <div class="mb-2">
        <logo />
      </div>
      <div class="title_container">
        <p class="title">{{ isPasswordMode ? 'Connexion' : 'Bienvenue' }}</p>
        <span class="subtitle">{{
          isPasswordMode
            ? 'Connectez-vous avec votre email et mot de passe'
            : 'Connectez-vous rapidement avec Google ou recevez un code par email'
        }}</span>
      </div>

      <!-- Mode Principal: Google + OTP -->
      <template v-if="!isPasswordMode">
        <!-- Google Button -->
        <button @click.prevent="signInWithGoogle" type="button" class="sign-in_ggl" :disabled="loading">
          <svg height="18" width="18" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <g transform="matrix(.727273 0 0 .727273 -.954545 -1.45455)">
              <path fill="#fbbc05" d="M0 37V11l17 13z"></path>
              <path fill="#ea4335" d="M0 11l17 13 7-6.1L48 14V0H0z"></path>
              <path fill="#34a853" d="M0 37l30-23 7.9 1L48 0v48H0z"></path>
              <path fill="#4285f4" d="M48 48L17 24l-4-3 35-10z"></path>
            </g>
          </svg>
          <span>Continuer avec Google</span>
        </button>

        <div class="separator">
          <hr class="line" />
          <span>ou</span>
          <hr class="line" />
        </div>

        <!-- OTP Mode -->
        <template v-if="!showOTPInput">
          <v-text-field
            label="Email"
            color="secondary"
            v-model="email"
            prepend-inner-icon="mdi-email-outline"
            placeholder="votre@email.com"
            outlined
            :rules="[
              (v) => !!v || 'Email requis',
              (v) => /.+@.+\..+/.test(v) || 'Email invalide',
            ]"
            class="w-100"
            @keyup.enter="requestOTP"
          />

          <button
            :disabled="!isEmailValid || loading"
            @click.prevent="requestOTP"
            type="button"
            class="sign-in_btn"
            :class="!isEmailValid ? 'disable-btn' : 'primary lighten-1'"
          >
            <span v-show="!loading">Recevoir un code de connexion</span>
            <v-btn small v-show="loading" class="elevation-0 w-100 disable-btn primary lighten-1" icon :loading="true"></v-btn>
          </button>
        </template>

        <!-- OTP Code Input -->
        <template v-else>
          <p class="otp-sent-message mb-2">
            Code envoyé à <strong>{{ email }}</strong>
          </p>
          <p class="spam-notice mb-3">
            <v-icon small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
            Vérifiez vos spams si vous ne voyez pas l'email. Marquez-le comme "Non spam" pour recevoir les prochains codes.
          </p>

          <v-text-field
            label="Code de v&eacute;rification"
            color="secondary"
            v-model="otpCode"
            prepend-inner-icon="mdi-key"
            placeholder="000000"
            outlined
            maxlength="6"
            :rules="[(v) => !!v || 'Code requis', (v) => v.length === 6 || 'Code &agrave; 6 chiffres']"
            class="w-100 otp-input"
            @keyup.enter="verifyOTP"
          />

          <button
            :disabled="otpCode.length !== 6 || loading"
            @click.prevent="verifyOTP"
            type="button"
            class="sign-in_btn"
            :class="otpCode.length !== 6 ? 'disable-btn' : 'primary lighten-1'"
          >
            <span v-show="!loading">V&eacute;rifier</span>
            <v-btn small v-show="loading" class="elevation-0 w-100 disable-btn primary lighten-1" icon :loading="true"></v-btn>
          </button>

          <a class="note primary--text" href="#" @click.prevent="resetOTPFlow">
            Changer d'email
          </a>

          <a class="note primary--text" href="#" @click.prevent="requestOTP" v-if="!loading">
            Renvoyer le code
          </a>
        </template>

        <a class="note primary--text mt-3" href="#" @click.prevent="togglePasswordMode">
          Connexion avec mot de passe
        </a>
      </template>

      <!-- Mode Password (classique) -->
      <template v-else>
        <v-text-field
          label="Email"
          color="secondary"
          v-model="authModel.username"
          prepend-inner-icon="mdi-gmail"
          placeholder="votre@email.com"
          outlined
          :rules="[
            (v) => !!v || 'Email requis',
            (v) => /.+@.+\..+/.test(v) || 'Email invalide',
          ]"
          class="w-100"
        />
        <v-text-field
          label="Mot de passe"
          v-model="authModel.password"
          type="password"
          color="secondary"
          prepend-inner-icon="mdi-lock-outline"
          placeholder="Votre mot de passe"
          :rules="[(v) => !!v || 'Mot de passe requis']"
          outlined
          class="w-100"
          @keyup.enter="signIn"
        />

        <button
          :disabled="!valid || loading"
          @click.prevent="signIn"
          type="submit"
          class="sign-in_btn"
          :class="!valid ? 'disable-btn' : 'primary lighten-1'"
        >
          <span v-show="!loading">Se connecter</span>
          <v-btn small v-show="loading" class="elevation-0 w-100 disable-btn primary lighten-1" icon :loading="true"></v-btn>
        </button>

        <router-link class="note primary--text" to="/auth/forget-password">Mot de passe oubli&eacute; ?</router-link>

        <a class="note primary--text" href="#" @click.prevent="togglePasswordMode">
          &larr; Retour &agrave; la connexion rapide
        </a>
      </template>

      <a class="note primary--text" href="#" @click.prevent="goTo('/auth/signup')" v-if="isPasswordMode">
        Pas de compte ? S'inscrire
      </a>
    </v-form>
  </div>
</template>

<script>
import Logo from "@/components/identifier/AuthLogo.vue";
import { login, signinWithGoogle, requestOTPLogin, verifyOTPLogin } from "@/api/user";
import { methods } from "@/utils/constant.js";
import Cookies from "js-cookie";
import { redirectWithFilteredQuery } from "@/utils/navigation";

export default {
  components: { Logo },
  data() {
    return {
      valid: false,
      loading: false,
      isPasswordMode: false,
      showOTPInput: false,
      email: "",
      otpCode: "",
      authModel: {},
    };
  },
  computed: {
    isEmailValid() {
      return this.email && /.+@.+\..+/.test(this.email);
    },
  },
  methods: {
    goTo(path) {
      const allowedParams = ["client_id", "redirect_uri", "next_link"];
      redirectWithFilteredQuery(path, this.$router, allowedParams);
    },

    togglePasswordMode() {
      this.isPasswordMode = !this.isPasswordMode;
      this.showOTPInput = false;
      this.otpCode = "";
    },

    resetOTPFlow() {
      this.showOTPInput = false;
      this.otpCode = "";
    },

    async signInWithGoogle() {
      this.loading = true;
      try {
        await signinWithGoogle();
      } catch (error) {
        console.error("Google signin error", error);
        methods.dispatchError(
          this.$store,
          error?.response?.data?.message || "Erreur de connexion Google"
        );
      }
      this.loading = false;
    },

    async requestOTP() {
      if (!this.isEmailValid) return;

      this.loading = true;
      try {
        const response = await requestOTPLogin(this.email);
        this.showOTPInput = true;
        methods.dispatchSuccess(
          this.$store,
          response.isNewUser
            ? "Code envoy&eacute; ! Un nouveau compte sera cr&eacute;&eacute;."
            : "Code envoy&eacute; &agrave; votre email"
        );
      } catch (error) {
        console.error("OTP request error", error);
        methods.dispatchError(
          this.$store,
          error?.response?.data?.message || "Erreur lors de l'envoi du code"
        );
      }
      this.loading = false;
    },

    async verifyOTP() {
      if (this.otpCode.length !== 6) return;

      this.loading = true;
      try {
        const response = await verifyOTPLogin(this.email, this.otpCode);

        if (response.isNewUser) {
          methods.dispatchSuccess(this.$store, "Compte cr&eacute;&eacute; avec succ&egrave;s !");
        } else {
          methods.dispatchSuccess(this.$store, "Connexion r&eacute;ussie !");
        }

        // Redirect to user account
        this.handleSuccessfulLogin();
      } catch (error) {
        console.error("OTP verify error", error);
        methods.dispatchError(
          this.$store,
          error?.response?.data?.message || "Code invalide ou expir&eacute;"
        );
      }
      this.loading = false;
    },

    async signIn() {
      if (this.$refs.form.validate()) {
        this.loading = true;

        try {
          const response = await login({
            email: this.authModel.username,
            password: this.authModel.password,
          });

          if (response.mfaRequired) {
            // MFA required - redirect to verification page with query params preserved
            const urlParams = new URLSearchParams(window.location.search);
            const mfaPath = `/auth/double-authentification-verification${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
            this.$router.push(mfaPath);
          } else {
            // No MFA needed - direct login
            localStorage.setItem("user-token", response.token);
            localStorage.setItem("redirect-to-user-account", "yes");
            this.handleSuccessfulLogin();
          }
        } catch (error) {
          console.error("login error", error);
          methods.dispatchError(
            this.$store,
            error?.response?.data?.message || "Email ou mot de passe incorrect"
          );
          await methods.sleep(3000);
        }
        this.loading = false;
      }
    },

    handleSuccessfulLogin() {
      // Check for redirect params
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUri = urlParams.get("redirect_uri");
      const nextLink = urlParams.get("next_link");

      if (redirectUri) {
        window.location.href = redirectUri;
      } else if (nextLink) {
        window.location.href = nextLink;
      } else {
        // Default redirect
        location.reload();
      }
    },

    checkAffiliationCode() {
      const urlParams = new URLSearchParams(window.location.search);
      let affiliationCode = urlParams.get("affiliation_code");
      if (affiliationCode) {
        this.setAffiliationCodeCookie(affiliationCode);
      }
    },

    setAffiliationCodeCookie(affiliationCode) {
      Cookies.set("affiliation_code", affiliationCode, { expires: 3 });
    },

    getAffiliationCodeFromCookie() {
      return Cookies.get("affiliation_code");
    },
  },
  mounted() {
    this.checkAffiliationCode();
  },
};
</script>

<style lang="scss" scoped>
.form_container {
  width: fit-content;
  max-width: 400px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 50px 40px 20px 40px;
  background-color: #ffffff;
  box-shadow: 0px 106px 42px rgba(0, 0, 0, 0.01),
    0px 59px 36px rgba(0, 0, 0, 0.05), 0px 26px 26px rgba(0, 0, 0, 0.09),
    0px 7px 15px rgba(0, 0, 0, 0.1), 0px 0px 0px rgba(0, 0, 0, 0.1);
  border-radius: 11px;
  font-family: "Inter", sans-serif;
}

.title_container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #212121;
}

.subtitle {
  font-size: 0.725rem;
  max-width: 90%;
  text-align: center;
  line-height: 1.1rem;
  color: #8b8e98;
}

.sign-in_btn {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 7px;
  outline: none;
  color: #ffffff;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.sign-in_btn:hover:not(.disable-btn) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sign-in_ggl {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 7px;
  outline: none;
  color: #242424;
  border: 2px solid #e5e5e5;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.sign-in_ggl:hover {
  border-color: #4285f4;
  background: #f8fafc;
}

.separator {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  color: #8b8e98;
  font-size: 0.75rem;
}

.separator .line {
  display: block;
  flex: 1;
  height: 1px;
  border: 0;
  background-color: #e8e8e8;
}

.note {
  font-size: 0.75rem;
  color: #8b8e98;
  text-decoration: underline;
  cursor: pointer;
}

.note:hover {
  color: #667eea;
}

.otp-sent-message {
  font-size: 0.85rem;
  color: #374151;
  text-align: center;
  margin: 0;
  padding: 8px 16px;
  background: #f0fdf4;
  border-radius: 6px;
  width: 100%;
}

.spam-notice {
  font-size: 0.75rem;
  color: #92400e;
  text-align: center;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  width: 100%;
  line-height: 1.4;
}

.otp-input ::v-deep input {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-weight: 600;
}

.disable-btn {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
